import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Loader2, UserRound } from "lucide-react";
import { TabPanel, TabView } from "primereact/tabview";
import { Button } from "primereact/button";

import styles from "./styles.module.css";

import {
    getBlockers,
    listUserCoachings,
    getCoachingSessions
} from "../../services/wisdomServices/growthService";

import { getSessionItem } from "../../utils/helper/session";

const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
        return "-";
    }

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric"
    }).format(parsedDate);
};

const formatDateTime = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
        return "-";
    }

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    }).format(parsedDate);
};

const formatStatus = (status = "") => {
    return status
        .replaceAll("_", " ")
        .replaceAll("-", " ")
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

const getInitials = (name = "") => {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
};

function MyGrowth() {
    const loggedUser = getSessionItem("User");

    const [blockers, setBlockers] = useState([]);
    const [coachings, setCoachings] = useState([]);

    const [loadingBlockers, setLoadingBlockers] = useState(false);
    const [loadingCoachings, setLoadingCoachings] = useState(false);

    const [blockerPage, setBlockerPage] = useState(1);
    const [blockerHasNextPage, setBlockerHasNextPage] = useState(false);

    const [expandedCoachings, setExpandedCoachings] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState({});

    const loadBlockers = useCallback(async (page = 1, append = false) => {
        if (!loggedUser?.id) return;

        try {
            setLoadingBlockers(true);

            const response = await getBlockers({
                ownerId: loggedUser.id,
                page,
                limit: 10
            });

            const items = response?.data ?? [];

            setBlockers((current) =>
                append
                    ? [...current, ...items]
                    : items
            );

            setBlockerPage(page);
            setBlockerHasNextPage(
                response?.pagination?.hasNextPage ?? false
            );
        } catch (error) {
            console.error("Failed to load blockers:", error);

            if (!append) {
                setBlockers([]);
            }
        } finally {
            setLoadingBlockers(false);
        }
    }, [loggedUser?.id]);

    const loadCoachings = useCallback(async () => {
        if (!loggedUser?.id) return;

        try {
            setLoadingCoachings(true);

            const response = await listUserCoachings(loggedUser.id);

            const items = Array.isArray(response)
                ? response
                : response?.data ?? response?.Details ?? [];

            setCoachings(
                items.map((item) => ({
                    ...item,
                    sessions: item.sessions ?? [],
                    sessionsLoaded: false
                }))
            );
        } catch (error) {
            console.error("Failed to load coachings:", error);
            console.error("API response:", error?.response?.data);

            setCoachings([]);
        } finally {
            setLoadingCoachings(false);
        }
    }, [loggedUser?.id]);

    const loadCoachingSessions = async (coachingId) => {
        try {
            setLoadingSessions((previous) => ({
                ...previous,
                [coachingId]: true
            }));

            const response = await getCoachingSessions(
                coachingId,
                1,
                50
            );

            setCoachings((previous) =>
                previous.map((item) =>
                    item.id === coachingId
                        ? {
                            ...item,
                            sessions: response?.data ?? [],
                            sessionsLoaded: true
                        }
                        : item
                )
            );
        } catch (error) {
            console.error(
                "Failed to load coaching sessions:",
                error
            );

            console.error(
                "API response:",
                error?.response?.data
            );
        } finally {
            setLoadingSessions((previous) => ({
                ...previous,
                [coachingId]: false
            }));
        }
    };

    const toggleCoaching = async (coachingId) => {
        const isExpanded =
            expandedCoachings.includes(coachingId);

        setExpandedCoachings((previous) =>
            isExpanded
                ? previous.filter((id) => id !== coachingId)
                : [...previous, coachingId]
        );

        if (isExpanded) {
            return;
        }

        const coaching = coachings.find(
            (item) => item.id === coachingId
        );

        if (!coaching?.sessionsLoaded) {
            await loadCoachingSessions(coachingId);
        }
    };

    useEffect(() => {
        if (!loggedUser?.id) return;

        loadBlockers();
        loadCoachings();
    }, [
        loggedUser?.id,
        loadBlockers,
        loadCoachings
    ]);

    const activeCoachings = useMemo(
        () =>
            coachings.filter(
                (item) => item.status !== "closed"
            ).length,
        [coachings]
    );

    const closedCoachings = useMemo(
        () =>
            coachings.filter(
                (item) => item.status === "closed"
            ).length,
        [coachings]
    );

    const getSeverityClass = (severity) => {
        switch (severity?.toLowerCase()) {
            case "low":
                return styles.low;

            case "medium":
                return styles.medium;

            case "high":
                return styles.high;

            case "urgent":
                return styles.urgent;

            default:
                return "";
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h2>My Growth</h2>

                    <p>
                        Track your blockers, coaching progress
                        and coaching sessions.
                    </p>
                </div>
            </div>

            <TabView className={styles.tabs}>
                {/* BLOCKERS */}
                <TabPanel
                    header="Blockers"
                    leftIcon="pi pi-exclamation-triangle"
                >
                    <div className={styles.section}>
                        <div className={styles.sectionHeader}>
                            <div>
                                <h3>My Blockers</h3>

                                <p>
                                    Blockers currently assigned
                                    to you.
                                </p>
                            </div>

                            <span className={styles.counter}>
                                {blockers.length}
                            </span>
                        </div>

                        {loadingBlockers &&
                        blockers.length === 0 ? (
                            <div className={styles.loading}>
                                <Loader2
                                    size={20}
                                    className={styles.spinner}
                                />

                                <span>
                                    Loading blockers...
                                </span>
                            </div>
                        ) : blockers.length > 0 ? (
                            <>
                                <div
                                    className={
                                        styles.blockerGrid
                                    }
                                >
                                    {blockers.map(
                                        (blocker) => (
                                            <div
                                                key={
                                                    blocker.id
                                                }
                                                className={
                                                    styles.blockerCard
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles.blockerHeader
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.blockerTitle
                                                        }
                                                    >
                                                        <span
                                                            className={
                                                                styles.blockerIcon
                                                            }
                                                        >
                                                            <i className="pi pi-exclamation-triangle" />
                                                        </span>

                                                        <div>
                                                            <h4>
                                                                {
                                                                    blocker.title
                                                                }
                                                            </h4>

                                                            <span
                                                                className={`${styles.severity} ${getSeverityClass(blocker.severity)}`}
                                                            >
                                                                {
                                                                    blocker.severity
                                                                }
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {blocker.sla && (
                                                        <div
                                                            className={
                                                                styles.sla
                                                            }
                                                        >
                                                            <i className="pi pi-clock" />

                                                            <div>
                                                                <span>
                                                                    SLA
                                                                </span>

                                                                <strong>
                                                                    {formatDateTime(
                                                                        blocker.sla
                                                                    )}
                                                                </strong>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                <p
                                                    className={
                                                        styles.description
                                                    }
                                                >
                                                    {blocker.description ||
                                                        "No description provided."}
                                                </p>

                                                <div
                                                    className={
                                                        styles.blockerFooter
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.metaItem
                                                        }
                                                    >
                                                        <i className="pi pi-user" />

                                                        <div>
                                                            <span>
                                                                Created
                                                                by
                                                            </span>

                                                            <strong>
                                                                {blocker.createdByName ||
                                                                    "-"}
                                                            </strong>
                                                        </div>
                                                    </div>

                                                    <div
                                                        className={
                                                            styles.metaItem
                                                        }
                                                    >
                                                        <i className="pi pi-calendar" />

                                                        <div>
                                                            <span>
                                                                Created
                                                                at
                                                            </span>

                                                            <strong>
                                                                {formatDateTime(
                                                                    blocker.createdAt
                                                                )}
                                                            </strong>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>

                                {blockerHasNextPage && (
                                    <div
                                        className={
                                            styles.loadMore
                                        }
                                    >
                                        <Button
                                            type="button"
                                            label="Load more"
                                            icon="pi pi-angle-down"
                                            outlined
                                            loading={
                                                loadingBlockers
                                            }
                                            onClick={() =>
                                                loadBlockers(
                                                    blockerPage +
                                                        1,
                                                    true
                                                )
                                            }
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className={styles.empty}>
                                <span
                                    className={
                                        styles.emptyIcon
                                    }
                                >
                                    <i className="pi pi-check-circle" />
                                </span>

                                <h4>No blockers</h4>

                                <p>
                                    You don't currently have any
                                    blockers assigned to you.
                                </p>
                            </div>
                        )}
                    </div>
                </TabPanel>

                {/* COACHINGS */}
                <TabPanel
                    header="Coachings"
                    leftIcon="pi pi-comments"
                >
                    <div
                        className={
                            styles.coachingSection
                        }
                    >
                        <div
                            className={
                                styles.coachingSectionHeader
                            }
                        >
                            <div>
                                <div
                                    className={
                                        styles.coachingTitleRow
                                    }
                                >
                                    <h3>My Coachings</h3>

                                    <span
                                        className={
                                            styles.totalBadge
                                        }
                                    >
                                        {coachings.length}{" "}
                                        {coachings.length === 1
                                            ? "item"
                                            : "items"}
                                    </span>
                                </div>

                                <p>
                                    Follow your coaching goals,
                                    progress and sessions.
                                </p>
                            </div>
                        </div>

                        <div
                            className={
                                styles.coachingSummary
                            }
                        >
                            <div
                                className={
                                    styles.coachingSummaryItem
                                }
                            >
                                <span
                                    className={
                                        styles.coachingSummaryValue
                                    }
                                >
                                    {activeCoachings}
                                </span>

                                <span
                                    className={
                                        styles.coachingSummaryLabel
                                    }
                                >
                                    Active
                                </span>
                            </div>

                            <div
                                className={
                                    styles.coachingSummaryDivider
                                }
                            />

                            <div
                                className={
                                    styles.coachingSummaryItem
                                }
                            >
                                <span
                                    className={
                                        styles.coachingSummaryValue
                                    }
                                >
                                    {closedCoachings}
                                </span>

                                <span
                                    className={
                                        styles.coachingSummaryLabel
                                    }
                                >
                                    Closed
                                </span>
                            </div>
                        </div>

                        <div
                            className={
                                styles.coachingList
                            }
                        >
                            {loadingCoachings ? (
                                <div
                                    className={
                                        styles.emptySessions
                                    }
                                >
                                    <Loader2
                                        size={20}
                                        className={
                                            styles.spinner
                                        }
                                    />

                                    Loading coachings...
                                </div>
                            ) : coachings.length === 0 ? (
                                <div
                                    className={
                                        styles.emptySessions
                                    }
                                >
                                    No coaching items found.
                                </div>
                            ) : (
                                coachings.map((item) => {
                                    const isExpanded =
                                        expandedCoachings.includes(
                                            item.id
                                        );

                                    const isClosed =
                                        item.status ===
                                        "closed";

                                    return (
                                        <article
                                            key={item.id}
                                            className={
                                                styles.coachingCard
                                            }
                                        >
                                            <div
                                                className={
                                                    styles.cardHeader
                                                }
                                                role="button"
                                                tabIndex={0}
                                                onClick={() =>
                                                    toggleCoaching(
                                                        item.id
                                                    )
                                                }
                                                onKeyDown={(
                                                    event
                                                ) => {
                                                    if (
                                                        event.key ===
                                                            "Enter" ||
                                                        event.key ===
                                                            " "
                                                    ) {
                                                        toggleCoaching(
                                                            item.id
                                                        );
                                                    }
                                                }}
                                            >
                                                <div
                                                    className={
                                                        styles.cardHeaderContent
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.cardTitleLine
                                                        }
                                                    >
                                                        <h2>
                                                            {
                                                                item.title
                                                            }
                                                        </h2>

                                                        <span
                                                            className={`${styles.statusBadge} ${
                                                                isClosed
                                                                    ? styles.completedBadge
                                                                    : styles.progressBadge
                                                            }`}
                                                        >
                                                            {formatStatus(
                                                                item.status
                                                            )}
                                                        </span>

                                                        {item.focusAreaName && (
                                                            <span
                                                                className={
                                                                    styles.categoryBadge
                                                                }
                                                            >
                                                                {
                                                                    item.focusAreaName
                                                                }
                                                            </span>
                                                        )}
                                                    </div>

                                                    <p
                                                        className={
                                                            styles.itemDescription
                                                        }
                                                    >
                                                        {
                                                            item.description
                                                        }
                                                    </p>

                                                    <div
                                                        className={
                                                            styles.peopleRow
                                                        }
                                                    >
                                                        {item.coachName && (
                                                            <div
                                                                className={
                                                                    styles.person
                                                                }
                                                            >
                                                                <span
                                                                    className={
                                                                        styles.coachLabel
                                                                    }
                                                                >
                                                                    <UserRound
                                                                        size={
                                                                            14
                                                                        }
                                                                    />
                                                                    Coach:
                                                                </span>

                                                                <span
                                                                    className={`${styles.avatar} ${styles.coachAvatar}`}
                                                                >
                                                                    {getInitials(
                                                                        item.coachName
                                                                    )}
                                                                </span>

                                                                <span>
                                                                    {
                                                                        item.coachName
                                                                    }
                                                                </span>
                                                            </div>
                                                        )}

                                                        {item.projectName && (
                                                            <div
                                                                className={
                                                                    styles.person
                                                                }
                                                            >
                                                                <span
                                                                    className={
                                                                        styles.coachLabel
                                                                    }
                                                                >
                                                                    <i className="pi pi-briefcase" />
                                                                    Project:
                                                                </span>

                                                                <span>
                                                                    {
                                                                        item.projectName
                                                                    }
                                                                </span>
                                                            </div>
                                                        )}

                                                        {item.followupDate && (
                                                            <div
                                                                className={
                                                                    styles.person
                                                                }
                                                            >
                                                                <span
                                                                    className={
                                                                        styles.coachLabel
                                                                    }
                                                                >
                                                                    <i className="pi pi-calendar" />
                                                                    Follow-up:
                                                                </span>

                                                                <span>
                                                                    {formatDate(
                                                                        item.followupDate
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div
                                                    className={
                                                        styles.headerActions
                                                    }
                                                >
                                                    {loadingSessions[
                                                        item.id
                                                    ] && (
                                                        <Loader2
                                                            size={
                                                                16
                                                            }
                                                            className={
                                                                styles.spinner
                                                            }
                                                        />
                                                    )}

                                                    <button
                                                        type="button"
                                                        className={
                                                            styles.expandButton
                                                        }
                                                        onClick={(
                                                            event
                                                        ) => {
                                                            event.stopPropagation();

                                                            toggleCoaching(
                                                                item.id
                                                            );
                                                        }}
                                                        aria-label={
                                                            isExpanded
                                                                ? "Collapse coaching"
                                                                : "Expand coaching"
                                                        }
                                                    >
                                                        {isExpanded ? (
                                                            <ChevronUp
                                                                size={
                                                                    20
                                                                }
                                                            />
                                                        ) : (
                                                            <ChevronDown
                                                                size={
                                                                    20
                                                                }
                                                            />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                            {isExpanded && (
                                                <div
                                                    className={
                                                        styles.cardContent
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.sessionsHeader
                                                        }
                                                    >
                                                        <h3>
                                                            Sessions
                                                        </h3>

                                                        <span>
                                                            {item
                                                                .sessions
                                                                ?.length ||
                                                                0}{" "}
                                                            {item
                                                                .sessions
                                                                ?.length ===
                                                            1
                                                                ? "session"
                                                                : "sessions"}
                                                        </span>
                                                    </div>

                                                    {loadingSessions[
                                                        item.id
                                                    ] ? (
                                                        <div
                                                            className={
                                                                styles.emptySessions
                                                            }
                                                        >
                                                            <Loader2
                                                                size={
                                                                    18
                                                                }
                                                                className={
                                                                    styles.spinner
                                                                }
                                                            />

                                                            Loading
                                                            sessions...
                                                        </div>
                                                    ) : item.sessions
                                                          ?.length >
                                                      0 ? (
                                                        <div
                                                            className={
                                                                styles.sessionsList
                                                            }
                                                        >
                                                            {item.sessions.map(
                                                                (
                                                                    session
                                                                ) => (
                                                                    <div
                                                                        key={
                                                                            session.id
                                                                        }
                                                                        className={
                                                                            styles.sessionCard
                                                                        }
                                                                    >
                                                                        <div
                                                                            className={
                                                                                styles.sessionTopRow
                                                                            }
                                                                        >
                                                                            <span
                                                                                className={
                                                                                    styles.sessionDate
                                                                                }
                                                                            >
                                                                                {formatDate(
                                                                                    session.createdAt
                                                                                )}
                                                                            </span>

                                                                            {session.status && (
                                                                                <span
                                                                                    className={
                                                                                        styles.sessionStatus
                                                                                    }
                                                                                >
                                                                                    {formatStatus(
                                                                                        session.status
                                                                                    )}
                                                                                </span>
                                                                            )}
                                                                        </div>

                                                                        <p
                                                                            className={
                                                                                styles.sessionDiscussion
                                                                            }
                                                                        >
                                                                            {
                                                                                session.discussion
                                                                            }
                                                                        </p>

                                                                        {session.actionItems && (
                                                                            <div
                                                                                className={
                                                                                    styles.actionList
                                                                                }
                                                                            >
                                                                                {session.actionItems
                                                                                    .split(
                                                                                        "\n"
                                                                                    )
                                                                                    .map(
                                                                                        (
                                                                                            action
                                                                                        ) =>
                                                                                            action.trim()
                                                                                    )
                                                                                    .filter(
                                                                                        Boolean
                                                                                    )
                                                                                    .map(
                                                                                        (
                                                                                            action,
                                                                                            index
                                                                                        ) => (
                                                                                            <div
                                                                                                key={`${session.id}-${index}`}
                                                                                                className={
                                                                                                    styles.actionItem
                                                                                                }
                                                                                            >
                                                                                                <span>
                                                                                                    →
                                                                                                </span>

                                                                                                <span>
                                                                                                    {
                                                                                                        action
                                                                                                    }
                                                                                                </span>
                                                                                            </div>
                                                                                        )
                                                                                    )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className={
                                                                styles.emptySessions
                                                            }
                                                        >
                                                            No coaching
                                                            sessions
                                                            logged yet.
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </article>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </TabPanel>
            </TabView>
        </div>
    );
}

export default MyGrowth;